import { useState, useEffect } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Shield, Cookie, X, Settings2 } from 'lucide-react';

interface ConsentState {
    necessary: boolean;
    preferences: boolean;
    statistics: boolean;
    timestamp: string;
}

export default function CookieConsent() {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const [consent, setConsent] = useState<ConsentState>({
        necessary: true,
        preferences: false,
        statistics: false,
        timestamp: new Date().toISOString()
    });

    useEffect(() => {
        const savedConsent = localStorage.getItem('cookie-consent');
        if (!savedConsent) {
            setIsVisible(true);
        }
    }, []);

    const handleAcceptAll = () => {
        const fullConsent = {
            necessary: true,
            preferences: true,
            statistics: true,
            timestamp: new Date().toISOString()
        };
        saveConsent(fullConsent);
    };

    const handleRejectAll = () => {
        const minimalConsent = {
            necessary: true,
            preferences: false,
            statistics: false,
            timestamp: new Date().toISOString()
        };
        saveConsent(minimalConsent);
    };

    const handleSavePreferences = () => {
        saveConsent({
            ...consent,
            timestamp: new Date().toISOString()
        });
    };

    const saveConsent = (data: ConsentState) => {
        localStorage.setItem('cookie-consent', JSON.stringify(data));
        setConsent(data);
        setIsVisible(false);
        // Dispatch event for other components to react
        window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: data }));
    };

    if (!isVisible) return null;

    return (
        <div className="fixed inset-x-0 bottom-0 z-[100] p-4 md:p-6 lg:p-8 animate-fade-in-up">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                {!showPreferences ? (
                    <div className="p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
                        <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <Cookie className="w-8 h-8" />
                        </div>
                        <div className="flex-1 text-center md:text-left">
                            <h3 className="text-xl font-bold text-slate-900 mb-2">{t('cookie.title')}</h3>
                            <p className="text-slate-600 leading-relaxed">
                                {t('cookie.desc')}
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                            <button
                                onClick={() => setShowPreferences(true)}
                                className="px-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                            >
                                <Settings2 className="w-4 h-4" />
                                {t('cookie.preferences')}
                            </button>
                            <button
                                onClick={handleAcceptAll}
                                className="px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-500/30 transition-all active:scale-[0.98]"
                            >
                                {t('cookie.accept_all')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="p-6 md:p-8 space-y-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Shield className="w-6 h-6 text-blue-600" />
                                <h3 className="text-xl font-bold text-slate-900">{t('cookie.preferences')}</h3>
                            </div>
                            <button
                                onClick={() => setShowPreferences(false)}
                                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5 text-slate-400" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* Necessary */}
                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 opacity-80">
                                <div className="flex-1 pr-4">
                                    <h4 className="font-bold text-slate-900">{t('cookie.necessary.title')}</h4>
                                    <p className="text-sm text-slate-500">{t('cookie.necessary.desc')}</p>
                                </div>
                                <div className="w-12 h-6 bg-blue-600 rounded-full relative flex items-center px-1">
                                    <div className="w-4 h-4 bg-white rounded-full shadow-sm translate-x-6" />
                                </div>
                            </div>

                            {/* Preferences */}
                            <label className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors">
                                <div className="flex-1 pr-4">
                                    <h4 className="font-bold text-slate-900">{t('cookie.prefs.title')}</h4>
                                    <p className="text-sm text-slate-500">{t('cookie.prefs.desc')}</p>
                                </div>
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={consent.preferences}
                                    onChange={(e) => setConsent({ ...consent, preferences: e.target.checked })}
                                />
                                <div className="w-12 h-6 bg-slate-300 rounded-full relative peer-checked:bg-blue-600 transition-colors flex items-center px-1">
                                    <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${consent.preferences ? 'translate-x-6' : 'translate-x-0'}`} />
                                </div>
                            </label>

                            {/* Statistics */}
                            <label className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors">
                                <div className="flex-1 pr-4">
                                    <h4 className="font-bold text-slate-900">{t('cookie.stats.title')}</h4>
                                    <p className="text-sm text-slate-500">{t('cookie.stats.desc')}</p>
                                </div>
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={consent.statistics}
                                    onChange={(e) => setConsent({ ...consent, statistics: e.target.checked })}
                                />
                                <div className="w-12 h-6 bg-slate-300 rounded-full relative peer-checked:bg-blue-600 transition-colors flex items-center px-1">
                                    <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${consent.statistics ? 'translate-x-6' : 'translate-x-0'}`} />
                                </div>
                            </label>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                            <button
                                onClick={handleRejectAll}
                                className="flex-1 px-6 py-3 border-2 border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-50 hover:border-slate-300 transition-all"
                            >
                                {t('cookie.reject_all')}
                            </button>
                            <button
                                onClick={handleSavePreferences}
                                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-500/20 transition-all active:scale-[0.98]"
                            >
                                {t('cookie.save')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
